// @flow

import { createSelector } from 'reselect';
import { stringify } from 'query-string';
import type {
  GlobalState,
  CampaignParams,
  TermFilters,
  RangeFilters,
  CustomFilters,
} from '../types';
import { getAssortmentStore } from './assortment';
import { getEntries } from '../../../helpers';
import { getUrlEncodedSort } from './sort';
import { getTranslation } from '../helpers/applyCustomFilter';

export const getFilters = (state: GlobalState) => (
  getAssortmentStore(state).filters
);

export const getTermFilters = (state: GlobalState) => (
  getFilters(state).term
);

export const getRangeFilters = (state: GlobalState) => (
  getFilters(state).range
);

export const getCustomFilters = (state: GlobalState) => (
  getFilters(state).custom
);

export const getCampaignParameters = (state: GlobalState): ?CampaignParams => (
  getFilters(state).campaign
);

export const getRangeFilter = (state: GlobalState, label: string) => (
  getRangeFilters(state)[label]
);

type FilterType = {
  id: string,
  type: string,
  value: string[] | string,
};

export const getSelectedFilters = createSelector<GlobalState, void, Array<any>, RangeFilters, TermFilters, CustomFilters, Function>( // eslint-disable-line max-len
  getRangeFilters,
  getTermFilters,
  getCustomFilters,
  getTranslation,
  (rangeFilters, termFilters, customFilters, translate) => {
    const custom: FilterType[] = customFilters.map(filter => ({
      id: filter,
      type: 'custom',
      value: translate(filter),
    }));
    const flattened: FilterType[] = Object.entries((termFilters: TermFilters))
      .reduce((acc, [label, values]) => acc.concat(
        // $FlowFixMe
        (values).map(value => ({
          id: label,
          type: 'term',
          value,
        })),
      ), custom);

    return Object.entries((rangeFilters: RangeFilters))
      // $FlowFixMe
      .reduce((acc, [label, { from, to }]) => acc.concat([
        {
          id: label,
          type: 'range',
          value: `€ ${from} - € ${to}`,
        },
      ]), flattened);
  },
);

export const getSelectedFiltersCount = createSelector<GlobalState, any, number, any>(
  getSelectedFilters,
  filters => filters.length,
);

export const isEmptyTermFilter = (state: GlobalState, label: string) => (
  getTermFilters(state)[label].length > 0
);

export const hasRangeFilters = (state: GlobalState) => (
  Object.keys(getRangeFilters(state)).length > 0
);

export const hasTermFilters = (state: GlobalState) => (
  Object.keys(getTermFilters(state)).length > 0
);

export const hasCustomFilters = (state: GlobalState) => (
  getCustomFilters(state).length > 0
);

export const hasCampaignParamters = (state: GlobalState): boolean => {
  const campaignParameters = getCampaignParameters(state);
  if (!campaignParameters) {
    return false;
  }
  return Object.keys(campaignParameters).length > 0;
};

export const isSelectedCustomFilter = (state: GlobalState, key: string): boolean => (
  getCustomFilters(state).includes(key)
);

export const hasFilters = (state: GlobalState) => hasTermFilters(state)
  || hasRangeFilters(state)
  || hasCustomFilters(state);

export const isSelectedTermFilter = (
  state: GlobalState,
  label: string,
  bucket: string,
): boolean => (
  new Set(getTermFilters(state)[label] || []).has(bucket)
);

export const getUrlEncodedTermFilters = (state: GlobalState) => {
  if (!hasTermFilters(state)) {
    return null;
  }
  // This is an error in flow-typed/npm/query-string_v6.x.x.js
  // $FlowFixMe
  return stringify(getTermFilters(state), { arrayFormat: 'bracket' });
};

export const getUrlEncodedCampaignParameters = (state: GlobalState): ?string => {
  if (!hasCampaignParamters(state)) {
    return null;
  }
  // This is an error in flow-typed/npm/query-string_v6.x.x.js
  // $FlowFixMe
  return stringify(getCampaignParameters(state));
};

export const getUrlEncodedRangeFilters = (state: GlobalState) => {
  if (!hasRangeFilters(state)) {
    return null;
  }
  // parse the range filter values as object
  const parseableRangeFilters = getEntries(getRangeFilters(state)).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: JSON.stringify(value),
    }),
    {},
  );
  return stringify(parseableRangeFilters);
};

export const getUrlEncodedCustomFilters = (state: GlobalState) => (
  stringify({ custom: getCustomFilters(state) }, { arrayFormat: 'bracket' })
);

export const getUrlEncodedAssortmentParams = (state: GlobalState) => [
  getUrlEncodedRangeFilters,
  getUrlEncodedCustomFilters,
  getUrlEncodedTermFilters,
  getUrlEncodedSort,
  getUrlEncodedCampaignParameters,
].map(fn => fn(state))
  .filter(string => {
    if (string) {
      return true;
    }
    return false;
  })
  .join('&');
